import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { format, parseISO } from 'date-fns';
import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { CircularProgress } from '@material-ui/core';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import { ARCHIVE_CORP_DOCUMENT } from '@/components/CorporateUsers/graphql';
import { linkedShippersByCarrierFrom } from '@/components/CorporateUsers/Carriers/utils';
import DocumentUploadModal from '@/components/CorporateUsers/modals/DocumentUploadModal';
import {
    AccentBar,
    Card,
    CardRow,
    EmptyState,
    Section,
    SectionHeader,
    SectionTitle,
    StatusPill,
} from '@/components/CorporateShipper/blocks';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { greys, onwardNavy, reds } from '@/styles/colors';
import { GET_CARRIER_DOCUMENTS } from './graphql';
import { documentSlotsFor } from './utils';

const OTHER_DOCUMENT_SLOT = { type: 'MISC', document: null };

const CardStrip = styled.div`
    align-items: center;
    background: #f9faf9;
    border-bottom: 1px solid ${greys.border};
    display: flex;
    gap: 8px;
    padding: 14px 24px;
`;

const StripTitle = styled.span`
    color: #3f3e4a;
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const StripNote = styled.span`
    color: ${greys.primary};
    font-size: 11.5px;
`;

const Row = styled(CardRow)`
    gap: 12px;
    padding: 16px 24px;

    & + & {
        border-top: 1px solid ${greys.border};
    }
`;

const RowTitle = styled.div`
    color: ${onwardNavy};
    font-size: 14.5px;
    font-weight: 800;
`;

const RowDetail = styled.div`
    color: ${({ alert }) => (alert ? reds.primary : greys.primary)};
    font-size: 12px;
    font-weight: ${({ alert }) => (alert ? 700 : 400)};
    margin-top: 6px;
`;

const RowActions = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: 12px;
`;

const compactButton = css`
    min-width: auto;
    padding: 6px 20px;
    font-size: 13px;
`;

const toDisplayDate = (isoDate) => {
    const date = parseISO(isoDate);

    return format(date, 'MM/dd/yyyy');
};

const SectionHeading = ({ title }) => (
    <SectionHeader>
        <AccentBar />
        <SectionTitle>{title}</SectionTitle>
    </SectionHeader>
);

const ViewButton = ({ document }) => (
    <SecondaryButton css={compactButton} as="a" href={document.link} target="_blank" rel="noopener noreferrer">
        View
    </SecondaryButton>
);

const UploadButton = ({ slot, secondary, children }) => {
    const { client } = useContext(UserContext);
    const [open, setOpen] = useState(false);
    const Button = secondary ? SecondaryButton : PrimaryButton;

    return (
        <>
            <Button css={compactButton} onClick={() => setOpen(true)}>
                {children}
            </Button>
            {open && (
                <DocumentUploadModal
                    open
                    onClose={() => setOpen(false)}
                    type={slot.type}
                    client_id={client.client_id}
                    partner_client_id={slot.partnerClientId ?? null}
                    existingDoc={slot.document}
                    contextLabel={slot.shipperName}
                />
            )}
        </>
    );
};

const RemoveButton = ({ document }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const [archiveDocument, { loading: removing }] = useMutation(ARCHIVE_CORP_DOCUMENT);

    const removeDocument = async () => {
        try {
            await archiveDocument({ variables: { document_id: document.document_id } });
            await callbacks.refetchCorpDocs();
        } catch (err) {
            Sentry.captureException(err);
            callbacks.setNotification({ severity: 'error', message: 'Error removing document' });
            return;
        }

        callbacks.setNotification({ severity: 'success', message: 'Document removed' });
    };

    return (
        <SecondaryButton css={compactButton} disabled={removing} onClick={removeDocument}>
            Remove
        </SecondaryButton>
    );
};

const SlotRow = ({ slot }) => {
    const { document } = slot;
    const title = slot.shipperName ? `${slot.label} — ${slot.shipperName}` : slot.label;

    const term = document?.term_months ? `${document.term_months}-month` : null;
    const signedOn = document?.signed_on ? toDisplayDate(document.signed_on) : null;
    const expiresOn = document?.expiration_date ? toDisplayDate(document.expiration_date) : null;
    let contractDetail = term ?? signedOn;
    if (term && signedOn) contractDetail = `${term} · ${signedOn}`;

    let detailText = '● not on file — required';
    if (slot.status === 'expired') detailText = `● expired ${expiresOn} — required`;
    if (slot.onFile) detailText = expiresOn ? `Expires ${expiresOn}` : contractDetail;

    let pill = null;
    if (slot.status === 'valid') pill = <StatusPill tone="on">On file</StatusPill>;
    if (slot.status === 'expiring') pill = <StatusPill>Expires in {slot.daysToExpiry}d</StatusPill>;

    return (
        <Row>
            <div>
                <RowTitle>{title}</RowTitle>
                {detailText && <RowDetail alert={!slot.onFile}>{detailText}</RowDetail>}
            </div>
            <RowActions>
                {pill}
                {document && <ViewButton document={document} />}
                <UploadButton slot={slot}>{document ? 'Replace' : 'Upload'}</UploadButton>
                {document && <RemoveButton document={document} />}
            </RowActions>
        </Row>
    );
};

const DocumentRow = ({ document, detail, children }) => (
    <Row>
        <div>
            <RowTitle>{document.name}</RowTitle>
            {detail && <RowDetail>{detail}</RowDetail>}
        </div>
        <RowActions>
            {children}
            <ViewButton document={document} />
            {!document.archived && <RemoveButton document={document} />}
        </RowActions>
    </Row>
);

const SlotCard = ({ title, slots }) => (
    <Card>
        <CardStrip>
            <StripTitle>{title}</StripTitle>
        </CardStrip>
        {slots.map((slot) => (
            <SlotRow key={slot.key} slot={slot} />
        ))}
    </Card>
);

const InsuranceContracts = () => {
    const corpAdmin = useContext(CorpAdminContext);
    const { client } = useContext(UserContext);
    const { organizationId, assignmentsByShipper, admin_clients } = corpAdmin.state;
    const { refetchCorpDocs } = corpAdmin.callbacks;
    const carrierId = client?.client_id;

    const { data, loading, error, refetch } = useQuery(GET_CARRIER_DOCUMENTS, {
        variables: { carrier_id: carrierId, organization_id: organizationId },
        skip: !carrierId || !organizationId,
        fetchPolicy: 'cache-and-network',
        onError: (err) => Sentry.captureException(err),
    });

    const slots = useMemo(() => {
        if (!data) return null;

        const clientsById = Object.fromEntries((admin_clients || []).map((c) => [c.client_id, c]));
        const linkedShippersByCarrier = linkedShippersByCarrierFrom(assignmentsByShipper, clientsById);
        const linkedShippers = linkedShippersByCarrier[carrierId] ?? [];

        return documentSlotsFor({ documents: data.documents, linkedShippers, clientsById });
    }, [data, admin_clients, assignmentsByShipper, carrierId]);

    if (loading && !data) {
        return (
            <EmptyState>
                <CircularProgress size={24} />
            </EmptyState>
        );
    }
    if (error && !data) return <EmptyState>Documents couldn't be loaded. Refresh to try again.</EmptyState>;
    if (!slots) return null;

    // DocumentUploadModal and RemoveButton refresh through refetchCorpDocs; this tab's own list must refresh too.
    const refetchDocuments = async () => {
        await Promise.all([refetchCorpDocs(), refetch()]);
    };
    const tabCorpAdmin = { ...corpAdmin, callbacks: { ...corpAdmin.callbacks, refetchCorpDocs: refetchDocuments } };

    return (
        <CorpAdminContext.Provider value={tabCorpAdmin}>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    padding: 24px 0;
                `}
            >
                <Section>
                    <SectionHeading title="Documents" />
                    <SlotCard title="Contracts" slots={slots.contracts} />
                    <SlotCard title="Insurance" slots={slots.insurance} />
                    <Card>
                        <CardStrip>
                            <StripTitle>Other Documents</StripTitle>
                            <StripNote>named at upload — a home for paperwork that fits no slot</StripNote>
                        </CardStrip>
                        {slots.others.map((document) => (
                            <DocumentRow key={document.document_id} document={document} />
                        ))}
                        <Row
                            css={css`
                                justify-content: flex-end;
                            `}
                        >
                            <UploadButton slot={OTHER_DOCUMENT_SLOT} secondary>
                                + Add document
                            </UploadButton>
                        </Row>
                    </Card>
                </Section>

                {slots.history.length > 0 && (
                    <Section>
                        <SectionHeading title="Document History" />
                        {slots.history.map((group) => (
                            <Card key={group.key}>
                                <CardStrip>
                                    <StripTitle>{group.label}</StripTitle>
                                </CardStrip>
                                {group.documents.map((document) => (
                                    <DocumentRow
                                        key={document.document_id}
                                        document={document}
                                        detail={`Effective Date: ${toDisplayDate(document.effectiveDate)}`}
                                    >
                                        {!document.archived && <StatusPill tone="on">On file</StatusPill>}
                                    </DocumentRow>
                                ))}
                            </Card>
                        ))}
                    </Section>
                )}
            </div>
        </CorpAdminContext.Provider>
    );
};

export default InsuranceContracts;
